import {
  Box,
  Button,
  Dialog,
  Field,
  Flex,
  Heading,
  Input,
  Stack,
  Table,
  Text
} from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { apiDelete, apiGet, apiPost, apiPut } from '../api.js';

const ArticleNameMaster = () => {
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);

  const fetchItems = async (query = '') => {
    try {
      const params = new URLSearchParams();
      if (query.trim()) params.set('search', query.trim());
      const res = await apiGet(`/master/article-names/list?${params.toString()}`);
      setItems(res.items || []);
    } catch (err) {
      alert(err.message || 'Failed to load master names');
    }
  };

  useEffect(() => {
    fetchItems();
  }, []);

  const openCreate = () => {
    setEditingItem(null);
    setName('');
    setOpen(true);
  };

  const openEdit = (item) => {
    setEditingItem(item);
    setName(item.name || '');
    setOpen(true);
  };

  const closeDialog = () => {
    setOpen(false);
    setEditingItem(null);
    setName('');
  };

  const handleSave = async () => {
    const trimmed = name.trim();
    if (!trimmed) {
      alert('Please enter a name');
      return;
    }

    setLoading(true);
    try {
      if (editingItem) {
        await apiPut(`/master/article-names/${editingItem._id}`, { name: trimmed });
      } else {
        await apiPost('/master/article-names', { name: trimmed });
      }
      closeDialog();
      fetchItems(search);
    } catch (err) {
      alert(err.message || 'Failed to save name');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this master name?')) return;
    try {
      await apiDelete(`/master/article-names/${id}`);
      fetchItems(search);
    } catch (err) {
      alert(err.message || 'Failed to delete name');
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchItems(search);
  };

  return (
    <Box>
      <Flex justify="space-between" align="center" mb={4}>
        <Heading size="md">Article Name Master</Heading>
        <Button colorPalette="blue" onClick={openCreate}>Add Name</Button>
      </Flex>

      <form onSubmit={handleSearch}>
        <Flex gap={2} mb={4}>
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search master names"
          />
          <Button type="submit">Search</Button>
          <Button
            variant="outline"
            onClick={() => {
              setSearch('');
              fetchItems('');
            }}
          >
            Clear
          </Button>
        </Flex>
      </form>

      <Box borderWidth="1px" borderRadius="lg" overflow="hidden">
        <Table.Root>
          <Table.Header>
            <Table.Row>
              <Table.ColumnHeader>Name</Table.ColumnHeader>
              <Table.ColumnHeader>Last Used</Table.ColumnHeader>
              <Table.ColumnHeader textAlign="right">Actions</Table.ColumnHeader>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {items.length === 0 ? (
              <Table.Row>
                <Table.Cell colSpan={3}>
                  <Text color="gray.500">No master names found.</Text>
                </Table.Cell>
              </Table.Row>
            ) : (
              items.map((item) => (
                <Table.Row key={item._id}>
                  <Table.Cell>{item.name}</Table.Cell>
                  <Table.Cell>{item.lastUsedAt ? new Date(item.lastUsedAt).toLocaleDateString() : '-'}</Table.Cell>
                  <Table.Cell textAlign="right">
                    <Button size="sm" mr={2} onClick={() => openEdit(item)}>Edit</Button>
                    <Button size="sm" colorPalette="red" onClick={() => handleDelete(item._id)}>Delete</Button>
                  </Table.Cell>
                </Table.Row>
              ))
            )}
          </Table.Body>
        </Table.Root>
      </Box>

      <Dialog.Root open={open} onOpenChange={(e) => !e.open && closeDialog()}>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content>
            <Dialog.Header>
              <Dialog.Title>{editingItem ? 'Edit Master Name' : 'Add Master Name'}</Dialog.Title>
            </Dialog.Header>
            <Dialog.CloseTrigger />
            <Dialog.Body>
              <Stack gap={3}>
                <Field.Root>
                  <Field.Label>Name</Field.Label>
                  <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Enter article name" />
                </Field.Root>
              </Stack>
            </Dialog.Body>
            <Dialog.Footer>
              <Button variant="ghost" onClick={closeDialog}>Cancel</Button>
              <Button colorPalette="blue" onClick={handleSave} loading={loading}>
                Save
              </Button>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </Dialog.Root>
    </Box>
  );
};

export default ArticleNameMaster;
